import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControlLabel,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { menuItems } from "../../layout/SideBar";
import {
  useAddRoleMutation,
  useUpdateRoleMutation,
  useLazyGetRoleByIdQuery,
} from "../../service/accessRole";
import { showError, showToast } from "../../constants/toasts";

const AddRole = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [addRole] = useAddRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [getRoleById] = useLazyGetRoleByIdQuery();
  const [prevueName, setPrevueName] = useState<string>("");

  const modules = useMemo(
    () =>
      menuItems
        ?.map((item) => ({ name: item?.title, sideBarId: item?.sideBarId }))
        ?.filter((item) => item?.name !== "Manage Sub-Admin"),
    []
  );

  const [roleName, setRoleName] = useState("");
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState(
    modules.map((module, index) => ({
      id: index + 1,
      name: module?.name,
      sideBarId: module?.sideBarId,
      view: false,
      add: false,
      edit: false,
      delete: false,
    }))
  );

  const handlePermissionChange = (id: number, field: string) => {
    setPermissions((prev) =>
      prev?.map((row) => {
        if (row.id !== id) return row;
        const current = Boolean(row[field as keyof typeof row]);
        const nextVal = !current;
        const next: typeof row = { ...row, [field]: nextVal } as typeof row;

        // If view is turned off, also remove add/edit/delete
        if (field === "view" && nextVal === false) {
          next.add = false;
          next.edit = false;
          next.delete = false;
        }

        // If add/edit/delete is turned on, ensure view is on
        if (
          (field === "add" || field === "edit" || field === "delete") &&
          nextVal === true
        ) {
          next.view = true;
        }

        // Couple add with edit: enabling add also enables edit; disabling add disables edit
        if (field === "add") {
          if (nextVal === true) {
            next.edit = true;
            next.view = true;
          } else {
            next.edit = false;
          }
        }

        return next;
      })
    );
  };

  const roleId = useMemo(() => {
    const idFromQuery = searchParams.get("id");
    // @ts-ignore
    const idFromState = (location?.state as any)?.id;
    return idFromQuery || idFromState || "";
  }, [location?.state, searchParams]);

  const isEdit = Boolean(roleId);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      if (!isEdit) return;
      try {
        const res = await getRoleById({ id: roleId }).unwrap();
        const data = res?.data as any;
        if (!isMounted || !data) return;
        setRoleName(data?.name || "");
        setPrevueName(data?.name || "");
        const byLabel: Record<string, any> = {};
        (data?.permissions || []).forEach((p: any) => {
          if (p?.label) byLabel[p.label] = p;
        });
        setPermissions((prev) =>
          prev?.map((row) => {
            const api = byLabel[row.name];
            if (!api) return row;
            return {
              ...row,
              view: Boolean(api?.isView),
              add: Boolean(api?.isAdd),
              edit: Boolean(api?.isEdit),
              delete: Boolean(api?.isDelete),
            };
          })
        );
      } catch (e: any) {
        showError(e?.data?.message || "Failed to fetch role data");
        navigate("/manage-subadmin/roles");
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [getRoleById, isEdit, roleId]);

  const handleSave = async () => {
    if (!roleName?.trim() || saving) return;
    setSaving(true);
    const payload = {
      ...(!isEdit && { name: roleName.trim() }),
      permissions: permissions.map((p) => ({
        label: p.name,
        sideBarId: p.sideBarId,
        isView: Boolean(p.view),
        isAdd: Boolean(p.add),
        isEdit: Boolean(p.add),
        isDelete: Boolean(p.delete),
      })),
    } as any;

    try {
      if (isEdit) {
        const res = await updateRole({ id: roleId, body: payload }).unwrap();
        const ok =
          res?.code === 200 || res?.statusCode === 200 || res?.status === 200;
        if (ok) {
          showToast(res?.message || "Role updated successfully");
          navigate("/manage-subadmin/roles");
        } else {
          showError(res?.message || "Failed to update role");
        }
      } else {
        const res = await addRole(payload).unwrap();
        const ok =
          res?.code === 200 || res?.statusCode === 200 || res?.status === 200;
        if (ok) {
          showToast(res?.message || "Role added successfully");
          navigate("/manage-subadmin/roles");
        } else {
          showError(res?.message || "Failed to add role");
        }
      }
    } catch (e: any) {
      showError(e?.data?.message || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>{isEdit ? "Edit Role" : "Add Role"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-subadmin")}>
            Manage Sub-Admin
          </button>{" "}
          -{" "}
          <button onClick={() => navigate("/manage-subadmin/roles")}>
            Manage Roles
          </button>{" "}
          - {isEdit ? "Edit Role" : "Add Role"}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_50">
              <label htmlFor="role">Role Name</label>
              <TextField
                id="role"
                placeholder="Enter Role Name"
                hiddenLabel
                disabled={isEdit}
                fullWidth
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
              />
            </div>
          </div>

          <TableContainer className="table_container mt_20">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Module Name</TableCell>
                  <TableCell align="center">View</TableCell>
                  <TableCell align="center">Add/Edit</TableCell>
                  {/* <TableCell align="center">Edit</TableCell> */}
                  <TableCell align="center">Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map((row) => (
                  <TableRow hover key={row.id}>
                    <TableCell>
                      {row.name.includes(" - ") ? (
                        <span style={{ paddingLeft: 20 }}>
                          {row.name.split(" - ")[1]}
                        </span>
                      ) : (
                        <strong>{row.name}</strong>
                      )}
                    </TableCell>
                    {["view", "add", "delete"].map((action) => (
                      <TableCell align="center" key={action}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(row[action as keyof typeof row])}
                              onChange={() =>
                                handlePermissionChange(row.id, action)
                              }
                              disabled={saving}
                            />
                          }
                          name="check"
                          label=""
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={handleSave}
              disabled={saving || !roleName.trim()}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddRole;
